/**
 * Three screens: sign in, the dashboard, withdraw.
 *
 * The guard is `beforeLoad` reading the session through the query cache, so a
 * signed-in partner never sees the login screen flash on a refresh and a
 * signed-out one never sees an empty dashboard.
 */

import type { QueryClient } from "@tanstack/react-query";
import {
	createRootRouteWithContext,
	createRoute,
	Outlet,
	redirect,
} from "@tanstack/react-router";
import { lazy, Suspense } from "react";
import { sessionQuery } from "./lib/api";
import { LoginPage } from "./pages/login";

/**
 * The two authenticated screens are split out of the initial chunk; login is
 * NOT.
 *
 * NFR-2 caps initial JS at 120 KB gzipped and says to "route-split beyond
 * that". Login is the landing screen for a signed-out partner, so hiding
 * weight behind it would turn the metric green without making the real first
 * load any faster — that is gaming the budget, not meeting it. Everything
 * behind the session is fair: by the time it loads, the partner has typed a
 * phone number and is waiting on a WhatsApp code.
 *
 * Splitting these two saves only ~2 KB today, which is not why it is here. It
 * is here so the pattern exists before the screens do: the SRS adds /refer,
 * /refer/bulk, /referrals/:id, /share and /help, and with 6.5 KB of headroom
 * the next eagerly-imported screen would fail the build. New authenticated
 * routes go through `lazyBoundary` and cost the initial bundle nothing.
 */
const DashboardPage = lazy(() =>
	import("./pages/dashboard").then((m) => ({ default: m.DashboardPage })),
);
const PayoutPage = lazy(() =>
	import("./pages/payout").then((m) => ({ default: m.PayoutPage })),
);
const ReferPage = lazy(() =>
	import("./pages/refer").then((m) => ({ default: m.ReferPage })),
);
const SharePage = lazy(() =>
	import("./pages/share").then((m) => ({ default: m.SharePage })),
);
const HelpPage = lazy(() =>
	import("./pages/help").then((m) => ({ default: m.HelpPage })),
);
const ReferralDetailPage = lazy(() =>
	import("./pages/referral-detail").then((m) => ({
		default: m.ReferralDetailPage,
	})),
);

/**
 * Blank, not a spinner. FR-P-2.5 wants the dashboard to paint last-known data
 * immediately on a repeat visit; a fallback spinner would flash in front of
 * cached content and undo exactly that.
 */
function lazyBoundary(node: React.ReactNode) {
	return <Suspense fallback={null}>{node}</Suspense>;
}

type RouterContext = { queryClient: QueryClient };

const rootRoute = createRootRouteWithContext<RouterContext>()({
	component: () => <Outlet />,
});

async function isAuthenticated(queryClient: QueryClient): Promise<boolean> {
	try {
		const session = await queryClient.ensureQueryData(sessionQuery);
		return session.authenticated;
	} catch {
		// A dead API is not a signed-in partner. Send them to /login, which
		// degrades to "ask for a code" rather than an empty dashboard.
		return false;
	}
}

/**
 * The guard, once. Every authenticated route uses it, so "what does a
 * signed-out partner see" has one answer rather than one per route — and a
 * route added later cannot quietly forget to check.
 */
const requireSession = async ({
	context,
}: {
	context: RouterContext;
}): Promise<void> => {
	if (!(await isAuthenticated(context.queryClient))) {
		throw redirect({ to: "/login" });
	}
};

const loginRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/login",
	beforeLoad: async ({ context }) => {
		if (await isAuthenticated(context.queryClient)) {
			throw redirect({ to: "/" });
		}
	},
	component: LoginPage,
});

const dashboardRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/",
	beforeLoad: requireSession,
	component: () => lazyBoundary(<DashboardPage />),
});

const payoutRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/payout",
	beforeLoad: requireSession,
	component: () => lazyBoundary(<PayoutPage />),
});

const referRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/refer",
	beforeLoad: requireSession,
	component: () => lazyBoundary(<ReferPage />),
});

const shareRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/share",
	beforeLoad: requireSession,
	component: () => lazyBoundary(<SharePage />),
});

const helpRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/help",
	beforeLoad: requireSession,
	component: () => lazyBoundary(<HelpPage />),
});

const referralDetailRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/referrals/$referralId",
	beforeLoad: requireSession,
	component: () => lazyBoundary(<ReferralDetailPage />),
});

export const routeTree = rootRoute.addChildren([
	loginRoute,
	dashboardRoute,
	payoutRoute,
	referRoute,
	shareRoute,
	helpRoute,
	referralDetailRoute,
]);
